/**
 * "Import" brand assets: drag-and-drop or pick files (images, short videos), choose a kind per file
 * (auto-guessed), optional name + shared tags, then upload one by one with per-file progress.
 *
 * Flow: uploadFile(file, {brandId, purpose:"brand_asset", onProgress}) → POST /api/uploads → {key, url}
 *   → assets.upload {brandId, storageKey, kind, name?, tags} → {asset: BrandAsset, deduped}
 *   (retries with `url` when the server refuses the upload key's prefix — see creative/upload.ts).
 * Fonts: assets.upload accepts font/*, but /api/uploads only sniffs images + video today, so fonts are
 * not offered here yet.
 */
import { CheckCircle2, CircleAlert, FileType2, Film, Image as ImageIcon, UploadCloud, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { errorMessage, isCancelled, uploadFile } from "@/api/client";
import { useRunTool } from "@/api/hooks";
import { toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { Button } from "../ui/Button";
import { Field, Input, SelectPill } from "../ui/Form";
import { Modal } from "../ui/Overlay";
import { Progress } from "../ui/Page";
import { Spinner } from "../ui/Feedback";
import { withStorageRef } from "../creative/upload";
import { formatBytes, readMediaDimensions, stripExt, useTopmostClose } from "../creative/hooks";
import { ASSET_KIND_SINGULAR, ASSET_KINDS, guessKind, type AssetKind } from "./normalize";
import { safePreviewSrc } from "@/lib/safe-url";

const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm";

interface Item {
  id: string;
  file: File;
  preview: string | null;
  kind: AssetKind;
  name: string;
  state: "ready" | "uploading" | "saving" | "done" | "error";
  progress: number;
  error?: string;
  deduped?: boolean;
}

const isUploadable = (i: Item) => i.file.size <= MAX_BYTES && /^image\/(png|jpeg|webp|gif)$|^video\/(mp4|quicktime|webm)$/.test(i.file.type);

export function AssetImportModal({
  open,
  onClose,
  brandId,
  defaultKind,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  brandId: string;
  defaultKind?: AssetKind;
  onUploaded?: (ids: string[]) => void;
}) {
  const { marker, close } = useTopmostClose(onClose);
  const [items, setItems] = useState<Item[]>([]);
  const [tags, setTags] = useState("");
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const runTool = useRunTool();

  useEffect(() => {
    if (!open) {
      setItems((prev) => {
        for (const i of prev) if (i.preview) URL.revokeObjectURL(i.preview);
        return [];
      });
      setTags("");
      setBusy(false);
    }
  }, [open]);

  const add = async (files: FileList | File[]) => {
    const next: Item[] = [];
    for (const file of Array.from(files)) {
      const supported = /^image\/(png|jpeg|webp|gif)$|^video\/(mp4|quicktime|webm)$/.test(file.type);
      const tooBig = file.size > MAX_BYTES;
      const visual = file.type.startsWith("image/") || file.type.startsWith("video/");
      const dims = visual ? await readMediaDimensions(file) : null;
      next.push({
        id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        preview: visual ? URL.createObjectURL(file) : null,
        kind: defaultKind ?? guessKind(file, dims),
        name: stripExt(file.name),
        state: tooBig || !supported ? "error" : "ready",
        progress: 0,
        error: !supported ? "Unsupported type — PNG, JPG, WebP, GIF, MP4, MOV or WebM" : tooBig ? `Larger than ${formatBytes(MAX_BYTES)}` : undefined,
      });
    }
    setItems((prev) => [...prev, ...next]);
  };

  const patch = (id: string, p: Partial<Item>) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...p } : i)));

  const upload = async () => {
    setBusy(true);
    const tagList = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const ids: string[] = [];
    let done = 0;
    let failed = 0;
    for (const it of items) {
      if (it.state !== "ready" && it.state !== "error") continue;
      if (!isUploadable(it)) continue;
      patch(it.id, { state: "uploading", error: undefined, progress: 0 });
      try {
        const media = await uploadFile(it.file, { brandId, purpose: "brand_asset", onProgress: (f) => patch(it.id, { progress: f }) });
        patch(it.id, { state: "saving", progress: 1 });
        const res = await withStorageRef(media, (ref) =>
          runTool<Record<string, unknown>>("assets.upload", { brandId, ...ref, kind: it.kind, ...(it.name.trim() ? { name: it.name.trim() } : {}), tags: tagList }, { silentError: true }),
        );
        const asset = res && typeof res === "object" && res.asset && typeof res.asset === "object" ? (res.asset as Record<string, unknown>) : res;
        const id = asset && typeof asset.id === "string" ? asset.id : null;
        if (id) ids.push(id);
        done += 1;
        patch(it.id, { state: "done", deduped: res?.deduped === true });
      } catch (e) {
        if (isCancelled(e)) {
          patch(it.id, { state: "ready" });
          break;
        }
        failed += 1;
        patch(it.id, { state: "error", error: errorMessage(e) });
      }
    }
    setBusy(false);
    if (done) toast({ tone: failed ? "warning" : "success", title: `Imported ${done} asset${done > 1 ? "s" : ""}${failed ? ` · ${failed} failed` : ""}` });
    else if (failed) toast({ tone: "danger", title: "Import failed", body: "See the error next to each file." });
    onUploaded?.(ids);
    if (done && !failed) window.setTimeout(onClose, 250);
  };

  const pending = items.filter((i) => i.state === "ready" || (i.state === "error" && isUploadable(i))).length;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : close}
      size="lg"
      className={marker}
      title="Import brand assets"
      description="Upload B-roll and UGC clips, logos, product shots or screenshots. Select them in Studio for video or static ads."
      footer={
        <>
          <span className="mr-auto text-[12px] text-muted">{items.length ? `${items.length} file${items.length > 1 ? "s" : ""}` : "PNG, JPG, WebP, GIF, MP4, MOV, WebM · up to 25MB"}</span>
          <Button variant="ghost" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!pending} loading={busy} icon={<UploadCloud className="size-3.5" />} onClick={() => void upload()}>
            Import{pending ? ` ${pending}` : ""}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            if (e.dataTransfer.files.length) void add(e.dataTransfer.files);
          }}
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-8 text-center transition-colors",
            drag ? "border-primary bg-primary-soft" : "border-line-strong hover:border-[#4a4a50] hover:bg-hover",
          )}
        >
          <span className="grid size-10 place-items-center rounded-full border border-line-strong text-muted">
            <UploadCloud className="size-5" strokeWidth={1.5} />
          </span>
          <span className="text-[13.5px] font-medium">Click or drag and drop to upload</span>
          <span className="text-[12px] text-muted">Transparent PNGs show on a checkerboard — perfect for logos and product cut-outs.</span>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              if (e.target.files?.length) void add(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {items.length > 0 && (
          <ul className="flex flex-col gap-2">
            {items.map((it) => (
              <li key={it.id} className={cn("rounded-xl border bg-surface-2 p-2.5", it.state === "error" ? "border-danger/40" : "border-line")}>
                <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
                  <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-[#151517] text-muted">
                    {it.preview && it.file.type.startsWith("image/") ? (
                      <img src={safePreviewSrc(it.preview)} alt="" className="size-full object-contain" />
                    ) : it.file.type.startsWith("video/") ? (
                      <Film className="size-5" strokeWidth={1.5} />
                    ) : it.preview ? (
                      <ImageIcon className="size-5" strokeWidth={1.5} />
                    ) : (
                      <FileType2 className="size-5" strokeWidth={1.5} />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Input value={it.name} onChange={(e) => patch(it.id, { name: e.target.value })} disabled={it.state !== "ready" && it.state !== "error"} aria-label="Asset name" className="h-8" />
                    <div className="mt-1 truncate text-[11.5px] text-subtle">
                      {it.file.name} · {formatBytes(it.file.size)}
                      {it.state === "uploading" && ` · uploading ${Math.round(it.progress * 100)}%`}
                      {it.state === "saving" && " · adding to library…"}
                      {it.deduped && " · already in the library"}
                      {it.error && <span className="text-danger"> · {it.error}</span>}
                    </div>
                  </div>
                  <SelectPill size="sm" value={it.kind} options={ASSET_KINDS.map((k) => ({ value: k, label: ASSET_KIND_SINGULAR[k] }))} onChange={(k) => patch(it.id, { kind: k })} align="end" />
                  <span className="grid size-7 shrink-0 place-items-center">
                    {it.state === "uploading" || it.state === "saving" ? (
                      <Spinner size={16} className="text-lavender" />
                    ) : it.state === "done" ? (
                      <CheckCircle2 className="size-4.5 text-success" />
                    ) : it.state === "error" ? (
                      <CircleAlert className="size-4.5 text-danger" />
                    ) : (
                      <button
                        type="button"
                        aria-label={`Remove ${it.file.name}`}
                        className="grid size-7 place-items-center rounded-md text-muted hover:bg-hover hover:text-fg"
                        onClick={() => {
                          if (it.preview) URL.revokeObjectURL(it.preview);
                          setItems((prev) => prev.filter((x) => x.id !== it.id));
                        }}
                      >
                        <X className="size-4" />
                      </button>
                    )}
                  </span>
                </div>
                {(it.state === "uploading" || it.state === "saving") && <Progress value={it.progress * 100} className="mt-2.5 h-1" />}
              </li>
            ))}
          </ul>
        )}

        <Field label="Tags (optional)" hint="Comma-separated, applied to every file — e.g. berry, pouch, hero">
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="berry, pouch" />
        </Field>
      </div>
    </Modal>
  );
}
